import { Config, Data, Puck, usePuck } from "@measured/puck";
import "@measured/puck/puck.css";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./MailEditor.css";
import {
  HeroImageProps,
  TitleProps,
  SubtitleProps,
  TextProps,
  CtaProps,
  HyperlinkProps,
  ImageTextProps,
  DividerProps,
} from "./MailEditor.types";
import { generateEmailHtml } from "./utils/emailHtmlConverter";
import { convertHtmlToPuckData } from "./importer/htmlImportConverter";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useTranslation } from "react-i18next";

const EDITOR_MULTILANG_KEY = "notification-editor";

// Cattura il document esterno (outer page) al caricamento del modulo, prima che
// il componente venga mai renderizzato dentro un iframe override di Puck.
const outerDocument = typeof document !== "undefined" ? document : null;

export interface MailEditorProps {
  initialHtml?: string;
  notificationName?: string;
  templates?: Record<string, { description: string }>;
  languageLabels?: Record<string, string>;
  isEditMode?: boolean;
  onUpdateSecondaryTemplate?: (
    lang: string,
    html: string,
  ) => Promise<void> | void;
  onSave?: (html: string, retranslateAll: boolean) => void;
  onClose?: () => void;
}

const getTypographyAndSpacingFields = (t: (key: string) => string) => ({
  fontFamily: {
    type: "select",
    label: "Font Family",
    options: [
      // Sans-serif
      { label: "Open Sans", value: "open Sans Helvetica, Arial, sans-serif" },
      { label: "Arial", value: "Arial, Helvetica, sans-serif" },
      { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
      {
        label: "Helvetica Neue",
        value: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      },
      { label: "Verdana", value: "Verdana, Geneva, Tahoma, sans-serif" },
      { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
      { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
      {
        label: "Gill Sans",
        value: "'Gill Sans', 'Gill Sans MT', Calibri, sans-serif",
      },
      {
        label: "Calibri",
        value: "Calibri, Candara, Segoe, 'Segoe UI', Optima, Arial, sans-serif",
      },
      {
        label: "Segoe UI",
        value: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      },
      {
        label: "Lucida Sans",
        value: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif",
      },
      {
        label: "Century Gothic",
        value: "'Century Gothic', CenturyGothic, AppleGothic, sans-serif",
      },
      {
        label: "Franklin Gothic",
        value:
          "'Franklin Gothic Medium', 'Arial Narrow Bold', Arial, sans-serif",
      },
      // Serif
      { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
      { label: "Georgia", value: "Georgia, 'Times New Roman', Times, serif" },
      { label: "Garamond", value: "Garamond, 'Times New Roman', serif" },
      {
        label: "Palatino",
        value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
      },
      { label: "Book Antiqua", value: "'Book Antiqua', Palatino, serif" },
      { label: "Cambria", value: "Cambria, 'Times New Roman', serif" },
      { label: "Didot", value: "Didot, 'Didot LT STD', 'Hoefler Text', serif" },
      // Monospace
      { label: "Courier New", value: "'Courier New', Courier, monospace" },
      { label: "Lucida Console", value: "'Lucida Console', Monaco, monospace" },
      { label: "Consolas", value: "Consolas, 'Courier New', monospace" },
    ],
  },
  fontSize: {
    type: "select",
    label: "Font Size",
    options: [
      { label: "10px", value: "10px" },
      { label: "11px", value: "11px" },
      { label: "12px", value: "12px" },
      { label: "13px", value: "13px" },
      { label: "14px", value: "14px" },
      { label: "15px", value: "15px" },
      { label: "16px", value: "16px" },
      { label: "18px", value: "18px" },
      { label: "20px", value: "20px" },
      { label: "22px", value: "22px" },
      { label: "24px", value: "24px" },
      { label: "28px", value: "28px" },
      { label: "32px", value: "32px" },
      { label: "36px", value: "36px" },
      { label: "40px", value: "40px" },
      { label: "48px", value: "48px" },
    ],
  },
  lineHeight: {
    type: "select",
    label: t("line-spacing"),
    options: [
      { label: "Normale", value: "normal" },
      { label: "1", value: "1" },
      { label: "1.15", value: "1.15" },
      { label: "1.2", value: "1.2" },
      { label: "1.4", value: "1.4" },
      { label: "1.5", value: "1.5" },
      { label: "1.6", value: "1.6" },
      { label: "1.8", value: "1.8" },
      { label: "2", value: "2" },
      { label: "2.5", value: "2.5" },
      { label: "3", value: "3" },
    ],
  },
  textAlign: {
    type: "select",
    label: t("alignment"),
    options: [
      { label: "Sinistra", value: "left" },
      { label: "Centro", value: "center" },
      { label: "Destra", value: "right" },
    ],
  },
  paddingTop: {
    type: "select",
    label: t("padding-top"),
    options: [
      { label: "8px", value: "8px" },
      ...Array.from({ length: 21 }, (_, i) => ({
        label: `${i * 5}px`,
        value: `${i * 5}px`,
      })),
    ],
  },
  paddingBottom: {
    type: "select",
    label: t("padding-bottom"),
    options: [
      { label: "8px", value: "8px" },
      ...Array.from({ length: 21 }, (_, i) => ({
        label: `${i * 5}px`,
        value: `${i * 5}px`,
      })),
    ],
  },
  paddingLeft: {
    type: "select",
    label: t("padding-left"),
    options: [
      { label: "8px", value: "8px" },
      ...Array.from({ length: 21 }, (_, i) => ({
        label: `${i * 5}px`,
        value: `${i * 5}px`,
      })),
    ],
  },
  paddingRight: {
    type: "select",
    label: t("padding-right"),
    options: [
      { label: "8px", value: "8px" },
      ...Array.from({ length: 21 }, (_, i) => ({
        label: `${i * 5}px`,
        value: `${i * 5}px`,
      })),
    ],
  },
});

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};

const quillModulesWithLists = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QuillField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  modules?: object;
}> = ({ label, value, onChange, modules = quillModules }) => {
  const quillRef = useRef<ReactQuill>(null);
  // Tiene traccia dell'ultimo valore impostato per evitare sync inutili
  const prevValueRef = useRef<string>("");
  // Sopprime onChange durante aggiornamenti programmatici (undo/redo)
  const isProgrammaticRef = useRef<boolean>(false);

  // Inizializzazione al mount
  useEffect(() => {
    if (quillRef.current && value) {
      isProgrammaticRef.current = true;
      quillRef.current.getEditor().clipboard.dangerouslyPasteHTML(0, value);
      isProgrammaticRef.current = false;
      prevValueRef.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync su cambio esterno del valore (undo/redo di Puck)
  useEffect(() => {
    if (value === prevValueRef.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    isProgrammaticRef.current = true;
    editor.clipboard.dangerouslyPasteHTML(0, value ?? "");
    isProgrammaticRef.current = false;
    prevValueRef.current = value;
  }, [value]);

  // Sostituisce il dropdown palette di Quill con un <input type="color"> nativo:
  // intercetta il mousedown sul .ql-picker-label in fase di capture così il
  // dropdown di Quill non si apre mai e parte direttamente il color picker OS.
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const toolbarEl = (quill as any).container
      ?.previousElementSibling as HTMLElement | null;
    if (!toolbarEl) return;

    const patchPicker = (format: "color" | "background") => {
      const pickerEl = toolbarEl.querySelector(`.ql-${format}.ql-picker`);
      if (!pickerEl) return;
      const label = pickerEl.querySelector(
        ".ql-picker-label",
      ) as HTMLElement | null;
      if (!label) return;

      label.addEventListener(
        "mousedown",
        (e: Event) => {
          e.preventDefault();
          e.stopImmediatePropagation();

          const current = (quill.getFormat()[format] as string) || "";
          const input = document.createElement("input");
          input.type = "color";
          input.value = /^#[0-9a-fA-F]{6}$/.test(current)
            ? current
            : format === "color"
              ? "#000000"
              : "#ffffff";
          // Posiziona l'input sotto il pulsante cliccato così il picker OS
          // compare vicino al punto di click invece che nell'angolo 0,0
          const rect = label.getBoundingClientRect();
          input.style.cssText = `position:fixed;opacity:0;pointer-events:none;width:1px;height:1px;top:${rect.bottom}px;left:${rect.left}px;`;
          document.body.appendChild(input);
          input.addEventListener("input", (ev) => {
            quill.format(format, (ev.target as HTMLInputElement).value, "user");
          });
          input.addEventListener("change", () => input.remove());
          input.click();
        },
        true, // capture: intercetta prima del listener Quill che apre il dropdown
      );
    };

    patchPicker("color");
    patchPicker("background");
    const isWhiteColor = (c: unknown) => {
      const v = String(c).replace(/\s+/g, "").toLowerCase();
      return (
        v === "#fff" ||
        v === "#ffffff" ||
        v === "white" ||
        v === "rgb(255,255,255)" ||
        v === "rgba(255,255,255,1)"
      );
    };
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      (delta.ops || []).forEach((op: any) => {
        if (op.attributes?.color && isWhiteColor(op.attributes.color)) {
          delete op.attributes.color;
        }
      });
      return delta;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (newVal: string) => {
      if (isProgrammaticRef.current) return;
      prevValueRef.current = newVal;
      onChange(newVal);
    },
    [onChange],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        position: "relative",
      }}
    >
      <label style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>
        {label}
      </label>
      <div className="custom-quill-wrapper">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          onChange={handleChange}
          modules={modules}
        />
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderReactQuillField =
  (label: string, modules?: object) =>
  ({ value, onChange }: any) => (
    <QuillField
      label={label}
      value={value || ""}
      onChange={onChange}
      modules={modules}
    />
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
//const config: Config<any, any> = {
const getPuckConfig = (t: (key: string) => string): Config<any, any> => ({
  components: {
    HeroImage: {
      label: t("hero-image"),
      fields: {
        url: {
          type: "custom",
          render: ({ value, onChange }) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "var(--mail-editor-text)",
                }}
              >
                {t("upload-image")}
              </label>
              <input
                type="file"
                accept="image/*"
                style={{
                  fontSize: "13px",
                  padding: "6px",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                  backgroundColor: "var(--puck-color-white)",
                }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      if (ev.target?.result) {
                        onChange(ev.target.result as string);
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "var(--mail-editor-text)",
                  marginTop: "8px",
                }}
              >
                {t("or-insert-url")}
              </label>
              <input
                type="text"
                placeholder="https://..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "8px",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                  color: "var(--mail-editor-text)",
                  background: "var(--puck-color-white)",
                }}
              />
              {value && (
                <img
                  src={value}
                  alt="Preview"
                  style={{
                    marginTop: "8px",
                    maxWidth: "100%",
                    maxHeight: "120px",
                    objectFit: "contain",
                    border: "1px solid var(--mail-editor-border)",
                    borderRadius: "4px",
                  }}
                />
              )}
              <button
                onClick={() =>
                  onChange(
                    "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
                  )
                }
                style={{
                  fontSize: "12px",
                  padding: "5px 12px",
                  border: "1px solid #f87171",
                  borderRadius: "4px",
                  cursor: "pointer",
                  background: "transparent",
                  color: "#ef4444",
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#fee2e2")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {t("remove")}
              </button>
            </div>
          ),
        },
      },
      defaultProps: {
        url: "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
      } as HeroImageProps,
      render: (props: any) => {
        return (
          <div style={{ textAlign: "center", overflow: "hidden" }}>
            <img
              src={props.url}
              alt="HeroImage"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </div>
        );
      },
    },
    Title: {
      label: t("title"),
      fields: {
        text: {
          type: "custom",
          render: renderReactQuillField(t("text")),
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        text: t("title"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "32px",
        lineHeight: "1.2",
        color: "#000000",
        textAlign: "center",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as TitleProps,
      render: (props: any) => (
        <div
          style={{
            textAlign: props.textAlign as any,
            padding: `${props.paddingTop} ${props.paddingRight} ${props.paddingBottom} ${props.paddingLeft}`,
          }}
        >
          <h1
            style={{
              margin: 0,
              fontWeight: "normal",
              fontSize: props.fontSize,
              fontFamily: props.fontFamily,
              lineHeight: props.lineHeight || "1.2",
              color: props.color,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
            dangerouslySetInnerHTML={{ __html: props.text }}
          />
        </div>
      ),
    },
    Subtitle: {
      label: t("subtitle"),
      fields: {
        text: {
          type: "custom",
          render: renderReactQuillField(t("text")),
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        text: t("subtitle"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "22px",
        lineHeight: "1.4",
        color: "#000000",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as SubtitleProps,
      render: (props: any) => (
        <div
          style={{
            padding: `${props.paddingTop} ${props.paddingRight} ${props.paddingBottom} ${props.paddingLeft}`,
            textAlign: props.textAlign as any,
          }}
        >
          <div
            style={{
              fontFamily: props.fontFamily,
              fontSize: props.fontSize,
              lineHeight: props.lineHeight || "1.4",
              color: props.color,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
            dangerouslySetInnerHTML={{ __html: props.text }}
          />
        </div>
      ),
    },
    Text: {
      label: t("text"),
      fields: {
        text: {
          type: "custom",
          render: renderReactQuillField(t("text"), quillModulesWithLists),
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        text: t("text"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        lineHeight: "1.4",
        color: "#000000",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as TextProps,
      render: (props: any) => (
        <div
          style={{
            padding: `${props.paddingTop} ${props.paddingRight} ${props.paddingBottom} ${props.paddingLeft}`,
            textAlign: props.textAlign as any,
          }}
        >
          <div
            style={{
              fontFamily: props.fontFamily,
              fontSize: props.fontSize,
              lineHeight: props.lineHeight || "1.4",
              color: props.color,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
            dangerouslySetInnerHTML={{ __html: props.text }}
          />
        </div>
      ),
    },
    CTA: {
      label: t("button"),
      fields: {
        label: {
          type: "custom",
          render: renderReactQuillField(t("text")),
        },
        url: { type: "text", label: "URL Bottone" },
        bgColor: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                {t("button-color")}
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  value={value || "#414141"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={value || "#414141"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px",
                    width: "100px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ),
        },
        textColor: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                {t("text-color")}
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  value={value || "#ffffff"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={value || "#ffffff"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px",
                    width: "100px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ),
        },
        paddingVertical: {
          type: "select",
          label: "Altezza (padding verticale)",
          options: [
            { label: "4px", value: "4px" },
            { label: "6px", value: "6px" },
            { label: "8px", value: "8px" },
            { label: "10px", value: "10px" },
            { label: "12px", value: "12px" },
            { label: "14px", value: "14px" },
            { label: "16px", value: "16px" },
            { label: "20px", value: "20px" },
            { label: "24px", value: "24px" },
          ],
        },
        paddingHorizontal: {
          type: "select",
          label: "Larghezza (padding orizzontale)",
          options: [
            { label: "10px", value: "10px" },
            { label: "15px", value: "15px" },
            { label: "20px", value: "20px" },
            { label: "25px", value: "25px" },
            { label: "30px", value: "30px" },
            { label: "40px", value: "40px" },
            { label: "50px", value: "50px" },
            { label: "60px", value: "60px" },
            { label: "80px", value: "80px" },
          ],
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        label: "CTA",
        url: "",
        bgColor: "#414141",
        textColor: "#ffffff",
        paddingVertical: "10px",
        paddingHorizontal: "25px",
        textAlign: "center",
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        color: "#000000",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as CtaProps,
      render: (props: any) => {
        const btnStyle: React.CSSProperties = {
          display: "inline-block",
          background: props.bgColor || "#414141",
          color: props.textColor || "#ffffff",
          padding: `${props.paddingVertical || "10px"} ${props.paddingHorizontal || "25px"}`,
          fontSize: props.fontSize || "15px",
          fontFamily:
            props.fontFamily || "open Sans Helvetica, Arial, sans-serif",
          margin: 0,
        };
        return (
          <div
            style={{
              padding: `${props.paddingTop || "8px"} ${props.paddingRight || "25px"} ${props.paddingBottom || "8px"} ${props.paddingLeft || "25px"}`,
              textAlign: (props.textAlign || "center") as any,
            }}
          >
            {props.url && props.url !== "#" ? (
              <a
                href={props.url}
                style={{ ...btnStyle, textDecoration: "none" }}
                dangerouslySetInnerHTML={{ __html: props.label }}
              />
            ) : (
              <div
                style={btnStyle}
                dangerouslySetInnerHTML={{ __html: props.label }}
              />
            )}
          </div>
        );
      },
    },
    Hyperlink: {
      label: t("text-with-link"),
      fields: {
        preText: {
          type: "custom",
          render: renderReactQuillField(
            t("text-with-link"),
            quillModulesWithLists,
          ),
        },
        linkLabel: { type: "text", label: t("link-text") },
        url: { type: "text", label: "URL" },
        linkColor: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                {t("link-color")}
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  value={value || "#0066cc"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={value || "#0066cc"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px",
                    width: "100px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ),
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        preText: t("pre-text"),
        linkLabel: t("click-here"),
        url: "",
        linkColor: "#0066cc",
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        color: "#000000",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as HyperlinkProps,
      render: (props: any) => (
        <div
          style={{
            padding: `${props.paddingTop} ${props.paddingRight} ${props.paddingBottom} ${props.paddingLeft}`,
            textAlign: props.textAlign as any,
          }}
        >
          <span
            style={{
              fontFamily: props.fontFamily,
              fontSize: props.fontSize,
              color: props.color,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {props.preText && (
              <span dangerouslySetInnerHTML={{ __html: `${props.preText}` }} />
            )}
            <a
              href={props.url || "#"}
              style={{
                color: props.linkColor || "#0066cc",
                textDecoration: "underline",
              }}
            >
              {props.linkLabel}
            </a>
          </span>
        </div>
      ),
    },
    ImageWithText: {
      label: t("image-with-text"),
      fields: {
        layout: {
          type: "select",
          label: "Layout",
          options: [
            { label: t("image-up-text-down"), value: "image-top" },
            { label: t("image-left-text-right"), value: "image-left" },
            { label: t("image-right-text-left"), value: "image-right" },
          ],
        },
        imageUrl: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "var(--mail-editor-text)",
                }}
              >
                {t("image")}
              </label>
              <input
                type="file"
                accept="image/*"
                style={{
                  fontSize: "13px",
                  padding: "6px",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                  backgroundColor: "var(--puck-color-white)",
                }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      if (ev.target?.result)
                        onChange(ev.target.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <input
                type="text"
                placeholder="https://..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "8px",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                  color: "var(--mail-editor-text)",
                  background: "var(--puck-color-white)",
                }}
              />
              {value && (
                <img
                  src={value}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80px",
                    objectFit: "contain",
                    border: "1px solid var(--mail-editor-border)",
                    borderRadius: "4px",
                  }}
                />
              )}
              <button
                onClick={() =>
                  onChange(
                    "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
                  )
                }
                style={{
                  fontSize: "12px",
                  padding: "5px 12px",
                  border: "1px solid #f87171",
                  borderRadius: "4px",
                  cursor: "pointer",
                  background: "transparent",
                  color: "#ef4444",
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#fee2e2")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {t("remove")}
              </button>
            </div>
          ),
        },
        imageWidth: {
          type: "select",
          label: t("image-width"),
          options: [
            { label: "30%", value: "30%" },
            { label: "40%", value: "40%" },
            { label: "50%", value: "50%" },
            { label: "60%", value: "60%" },
            { label: "100%", value: "100%" },
          ],
        },
        text: {
          type: "custom",
          render: renderReactQuillField(t("text"), quillModulesWithLists),
        },
        ...(getTypographyAndSpacingFields(t) as any),
      },
      defaultProps: {
        layout: "image-left",
        imageUrl:
          "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
        imageWidth: "40%",
        text: t("text"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        lineHeight: "1.5",
        color: "#000000",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as ImageTextProps,
      render: (props: any) => {
        const imgPctStr = String(props.imageWidth || "40%").replace("%", "");
        const imgPct = parseInt(imgPctStr) || 40;
        const txtPct = 100 - imgPct;

        const pTop = props.paddingTop || "8px";
        const pBot = props.paddingBottom || "8px";
        const pLeft = props.paddingLeft || "25px";
        const pRight = props.paddingRight || "25px";

        const isTopLayout = props.layout === "image-top" || imgPct === 100;

        if (isTopLayout) {
          const imgEl = props.imageUrl ? (
            <img
              src={props.imageUrl}
              alt=""
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                border: 0,
                margin: "0 auto",
              }}
            />
          ) : null;
          const textEl = (
            <div
              style={{
                fontFamily: props.fontFamily,
                fontSize: props.fontSize,
                color: props.color,
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: props.lineHeight || "1.5",
              }}
              dangerouslySetInnerHTML={{ __html: props.text }}
            />
          );

          return (
            <div
              style={{
                padding: `${pTop} ${pRight} ${pBot} ${pLeft}`,
                textAlign: props.textAlign as any,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {imgEl && (
                <div
                  style={{
                    paddingBottom: props.text ? "20px" : "0",
                    width: `${imgPct}%`,
                    margin: "0 auto",
                  }}
                >
                  {imgEl}
                </div>
              )}
              {props.text && <div style={{ width: "100%" }}>{textEl}</div>}
            </div>
          );
        }

        const isLeft = props.layout !== "image-right";
        const imgEl = props.imageUrl ? (
          <img
            src={props.imageUrl}
            alt={props.imageAlt}
            style={{
              width: "100%",
              maxWidth: "100%",
              height: "auto",
              display: "block",
              border: 0,
              margin: isLeft ? "0 auto 0 0" : "0 0 0 auto",
            }}
          />
        ) : null;
        const textEl = (
          <div
            style={{
              fontFamily: props.fontFamily,
              fontSize: props.fontSize,
              color: props.color,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              lineHeight: props.lineHeight || "1.5",
            }}
            dangerouslySetInnerHTML={{ __html: props.text }}
          />
        );

        // Ensure the flex container mimics the vertical align top / middle of MJML by using align-items flex-start
        return (
          <div
            style={{
              padding: `${pTop} ${pRight} ${pBot} ${pLeft}`,
              display: "flex",
              flexDirection: isLeft ? "row" : "row-reverse",
              width: "100%",
              boxSizing: "border-box",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            {imgEl && (
              <div
                style={{
                  width: `${imgPct}%`,
                  flexBasis: `${imgPct}%`,
                  flexShrink: 0,
                  boxSizing: "border-box",
                  paddingRight: isLeft && props.text ? "20px" : "0",
                  paddingLeft: !isLeft && props.text ? "20px" : "0",
                }}
              >
                {imgEl}
              </div>
            )}
            {props.text && (
              <div
                style={{
                  width: `${txtPct}%`,
                  flexBasis: `${txtPct}%`,
                  flexShrink: 0,
                  boxSizing: "border-box",
                  textAlign: props.textAlign as any,
                }}
              >
                {textEl}
              </div>
            )}
          </div>
        );
      },
    },
    Divider: {
      label: t("separator"),
      fields: {
        borderColor: {
          type: "custom",
          render: ({ value, onChange }: any) => (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                {t("line-color")}
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="color"
                  value={value || "#cccccc"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    cursor: "pointer",
                    width: "32px",
                    height: "32px",
                    padding: "0",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
                <input
                  type="text"
                  value={value || "#cccccc"}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px",
                    width: "100px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ),
        },
        borderWidth: {
          type: "select",
          label: t("line-width"),
          options: [
            { label: "1px", value: "1px" },
            { label: "2px", value: "2px" },
            { label: "3px", value: "3px" },
            { label: "4px", value: "4px" },
            { label: "5px", value: "5px" },
          ],
        },
        borderStyle: {
          type: "select",
          label: t("line-style"),
          options: [
            { label: "Continua", value: "solid" },
            { label: "Tratteggiata", value: "dashed" },
            { label: "Puntinata", value: "dotted" },
          ],
        },
        paddingTop: {
          type: "select",
          label: t("padding-top"),
          options: [
            { label: "0px", value: "0px" },
            { label: "10px", value: "10px" },
            { label: "20px", value: "20px" },
            { label: "30px", value: "30px" },
            { label: "40px", value: "40px" },
            { label: "50px", value: "50px" },
          ],
        },
        paddingBottom: {
          type: "select",
          label: t("padding-bottom"),
          options: [
            { label: "0px", value: "0px" },
            { label: "10px", value: "10px" },
            { label: "20px", value: "20px" },
            { label: "30px", value: "30px" },
            { label: "40px", value: "40px" },
            { label: "50px", value: "50px" },
          ],
        },
        paddingLeft: {
          type: "select",
          label: t("padding-left"),
          options: [
            { label: "0px", value: "0px" },
            { label: "10px", value: "10px" },
            { label: "20px", value: "20px" },
            { label: "30px", value: "30px" },
          ],
        },
        paddingRight: {
          type: "select",
          label: t("padding-right"),
          options: [
            { label: "0px", value: "0px" },
            { label: "10px", value: "10px" },
            { label: "20px", value: "20px" },
            { label: "30px", value: "30px" },
          ],
        },
      },
      defaultProps: {
        borderColor: "#cccccc",
        borderWidth: "1px",
        borderStyle: "solid",
        paddingTop: "20px",
        paddingBottom: "20px",
        paddingLeft: "25px",
        paddingRight: "25px",
      } as DividerProps,
      render: (props: any) => (
        <div
          style={{
            padding: `${props.paddingTop} ${props.paddingRight} ${props.paddingBottom} ${props.paddingLeft}`,
          }}
        >
          <div
            style={{
              borderTop: `${props.borderWidth} ${props.borderStyle} ${props.borderColor}`,
              width: "100%",
            }}
          />
        </div>
      ),
    },
  },
  root: {
    fields: {
      bgColor: {
        type: "custom",
        render: ({ value, onChange }: any) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}
            >
              {t("email-background-color")}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="color"
                value={value || "#ffffff"}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  cursor: "pointer",
                  width: "32px",
                  height: "32px",
                  padding: "0",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
              <input
                type="text"
                value={value || "#ffffff"}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "6px",
                  width: "100px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        ),
      },
      bgImage: {
        type: "custom",
        render: ({ value, onChange }: any) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "var(--mail-editor-text)",
              }}
            >
              {t("external-background-image-url")}
            </label>
            <p
              style={{
                fontSize: "12px",
                color: "var(--mail-editor-text-muted)",
                margin: "0",
              }}
            >
              {t("outlook-compatibility")}
              (http/https).
            </p>
            <input
              type="text"
              placeholder="https://..."
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              style={{
                fontSize: "13px",
                padding: "8px",
                border: "1px solid var(--mail-editor-border)",
                borderRadius: "4px",
                color: "var(--mail-editor-text)",
                background: "var(--puck-color-white)",
              }}
            />
            {value && (
              <img
                src={value}
                alt="bg preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "60px",
                  objectFit: "cover",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                }}
              />
            )}
            <button
              onClick={() => onChange("")}
              style={{
                fontSize: "12px",
                padding: "5px 12px",
                border: "1px solid #f87171",
                borderRadius: "4px",
                cursor: "pointer",
                background: "transparent",
                color: "#ef4444",
                fontWeight: 600,
                alignSelf: "flex-start",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#fee2e2")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              {t("remove")}
            </button>
          </div>
        ),
      },

      bodyBgColor: {
        type: "custom",
        render: ({ value, onChange }: any) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}
            >
              {t("email-body-background-color")}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="color"
                value={value || "#ffffff"}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  cursor: "pointer",
                  width: "32px",
                  height: "32px",
                  padding: "0",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
              <input
                type="text"
                value={value || "#ffffff"}
                onChange={(e) => onChange(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "6px",
                  width: "100px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
            </div>
          </div>
        ),
      },
      bodyBgImage: {
        type: "custom",
        render: ({ value, onChange }: any) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "var(--mail-editor-text)",
              }}
            >
              {t("email-body-background-image")}
            </label>
            <p
              style={{
                fontSize: "12px",
                color: "var(--mail-editor-text-muted)",
                margin: "0",
              }}
            >
              {t("outlook-compatibility")}
              (http/https).
            </p>
            <input
              type="text"
              placeholder="https://..."
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              style={{
                fontSize: "13px",
                padding: "8px",
                border: "1px solid var(--mail-editor-border)",
                borderRadius: "4px",
                color: "var(--mail-editor-text)",
                background: "var(--puck-color-white)",
              }}
            />
            {value && (
              <img
                src={value}
                alt="bg preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "60px",
                  objectFit: "cover",
                  border: "1px solid var(--mail-editor-border)",
                  borderRadius: "4px",
                }}
              />
            )}
            <button
              onClick={() => onChange("")}
              style={{
                fontSize: "12px",
                padding: "5px 12px",
                border: "1px solid #f87171",
                borderRadius: "4px",
                cursor: "pointer",
                background: "transparent",
                color: "#ef4444",
                fontWeight: 600,
                alignSelf: "flex-start",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#fee2e2")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              {t("remove")}
            </button>
          </div>
        ),
      },
      rootWidth: {
        type: "select",
        label: t("email-width"),
        options: [
          { label: "Piccola (500px)", value: "500px" },
          { label: "Media (600px)", value: "600px" },
          { label: "Grande (700px)", value: "700px" },
          { label: "Extra (800px)", value: "800px" },
        ],
      },
    },
    defaultProps: {
      bgColor: "#ffffff",
      bgImage: "",
      bodyBgColor: "#ffffff",
      bodyBgImage: "",
      rootWidth: "600px",
    },
    render: ({
      children,
      bgColor,
      bgImage,
      bodyBgColor,
      bodyBgImage,
      rootWidth,
    }: any) => {
      const outerStyle: React.CSSProperties = {
        position: "relative",
        backgroundColor: bgColor || "#ffffff",
        minHeight: "100vh",
        padding: "20px 0",
      };
      const innerStyle: React.CSSProperties = {
        position: "relative",
        maxWidth: rootWidth || "600px",
        margin: "0 auto",
        backgroundColor: bodyBgColor || "#ffffff",
      };
      return (
        <div style={outerStyle}>
          {bgImage && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${bgImage})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center top",
                zIndex: 0,
              }}
            />
          )}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              maxWidth: rootWidth || "600px",
              margin: "0 auto",
            }}
          >
            <div style={innerStyle}>
              {bodyBgImage && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${bodyBgImage})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center top",
                    zIndex: 0,
                  }}
                />
              )}
              <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  // };
});

// const initialData: Data<any, any> = {
const getInitialData = (t: (key: string) => string): Data<any, any> => ({
  content: [
    {
      type: "HeroImage",
      props: {
        id: "img-1",
        url: "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
      },
    },
    {
      type: "Title",
      props: {
        id: "title-1",
        text: t("title"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "32px",
        color: "#000000",
        fontWeight: "normal",
        fontStyle: "normal",
        textDecoration: "none",
        textAlign: "center",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    },
    {
      type: "Subtitle",
      props: {
        id: "sub-1",
        text: t("subtitle"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "22px",
        color: "#000000",
        fontWeight: "normal",
        fontStyle: "normal",
        textDecoration: "none",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    },
    {
      type: "Text",
      props: {
        id: "text-1",
        text: t("text"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        color: "#000000",
        fontWeight: "normal",
        fontStyle: "normal",
        textDecoration: "none",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    },
    {
      type: "CTA",
      props: {
        id: "cta-1",
        label: "CTA",
        url: "",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    },
    {
      type: "Text",
      props: {
        id: "text-2",
        text: t("text"),
        fontFamily: "open Sans Helvetica, Arial, sans-serif",
        fontSize: "15px",
        color: "#000000",
        fontWeight: "normal",
        fontStyle: "normal",
        textDecoration: "none",
        textAlign: "left",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "25px",
        paddingRight: "25px",
      },
    },
    {
      type: "HeroImage",
      props: {
        id: "img-2",
        url: "https://car-mon.com/wp-content/uploads/2018/03/hero-placeholder.png",
      },
    },
  ],
  root: {
    props: {
      bgColor: "#ffffff",
      bgImage: "",
      bodyBgColor: "#ffffff",
      bodyBgImage: "",
      rootWidth: "600px",
    },
  },
});

// ── IframeResizer ─────────────────────────────────────────────────────────────
// Identico all'editoriale. scrollbar-gutter:stable riserva spazio per la
// scrollbar senza sovrapporsi al contenuto. padding:0 !important neutralizza
// il padding-right che MUI aggiunge al body tramite il suo scroll-lock
// (copiato da Puck's CopyHostStyles nell'iframe).
const IframeResizer: React.FC<{
  document?: Document;
  children: React.ReactNode;
}> = ({ document: iframeDoc, children }) => {
  useEffect(() => {
    if (!iframeDoc?.head) return;
    const style = iframeDoc.createElement("style");
    style.dataset.mailScrollFix = "1";
    style.textContent = `
      #frame-root { min-height: unset !important; height: auto !important; }
      html { color-scheme: light !important; height: 100% !important; overflow: hidden !important; }
      body { position: relative !important; height: 100% !important; overflow-y: auto !important; padding: 0 !important; }
    `;
    iframeDoc.head.appendChild(style);
    const mo = new MutationObserver(() => {
      if (iframeDoc.head.lastElementChild !== style) {
        iframeDoc.head.appendChild(style);
      }
    });
    mo.observe(iframeDoc.head, { childList: true });
    return () => {
      mo.disconnect();
      style.remove();
    };
  }, [iframeDoc]);
  return <>{children}</>;
};

// Item root nell'Outline — seleziona il wrapper email. Stile allineato ai Layer di Puck.
const RootOutlineItem: React.FC = () => {
  const { dispatch, appState } = usePuck();
  const isSelected = appState.ui.itemSelector === null;
  const { t } = useTranslation(EDITOR_MULTILANG_KEY);
  return (
    <button
      className={`outline-root-item${isSelected ? " outline-root-item--selected" : ""}`}
      onClick={() => dispatch({ type: "setUi", ui: { itemSelector: null } })}
      title={t("email-wrapper")}
      type="button"
    >
      <div className="outline-root-item__inner">
        <span className="outline-root-item__icon">
          <span className="outline-root-item__dot" />
        </span>
        <span className="outline-root-item__label">Root</span>
      </div>
    </button>
  );
};

interface SaveButtonProps {
  onPublish: (data: any) => void;
  theme: any;
  disabled?: boolean;
}

const SaveButton: React.FC<SaveButtonProps> = ({
  onPublish,
  theme,
  disabled,
}) => {
  const { appState } = usePuck();
  const { t } = useTranslation(EDITOR_MULTILANG_KEY);
  return (
    <button
      className="mail-editor-close-btn"
      onClick={() => onPublish(appState.data)}
      title={t("email-confirm")}
      disabled={disabled}
      style={{
        marginLeft: "8px",
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {t("confirm")}
    </button>
  );
};

interface ExportButtonProps {
  theme: any;
  onExport: (html: string) => void;
}

const ExportButton: React.FC<ExportButtonProps> = ({ theme, onExport }) => {
  const { appState } = usePuck();
  const { t } = useTranslation(EDITOR_MULTILANG_KEY);
  return (
    <button
      className="mail-editor-import-btn"
      onClick={() => onExport(generateEmailHtml(appState.data))}
      title={t("html-export")}
      style={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ marginRight: "6px" }}
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      {t("html-export")}
    </button>
  );
};

interface UpdateSecondaryTemplateButtonProps {
  theme: any;
  onUpdate: (html: string) => void;
}

const UpdateSecondaryTemplateButton: React.FC<
  UpdateSecondaryTemplateButtonProps
> = ({ theme, onUpdate }) => {
  const { appState } = usePuck();
  const { t } = useTranslation(EDITOR_MULTILANG_KEY);
  return (
    <button
      className="mail-editor-close-btn"
      onClick={() => onUpdate(generateEmailHtml(appState.data))}
      title="Aggiorna template secondario"
      style={{
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
      }}
    >
      {t("update")}
    </button>
  );
};

const MailEditor: React.FC<MailEditorProps> = ({
  initialHtml,
  notificationName,
  templates,
  languageLabels,
  isEditMode,
  onUpdateSecondaryTemplate,
  onSave,
  onClose,
}) => {
  const theme = useTheme(); // Added theme hook
  const { t } = useTranslation(EDITOR_MULTILANG_KEY);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState(t("html-in-clipboard"));
  const [puckKey, setPuckKey] = useState(0);

  const [confirmPanelOpen, setConfirmPanelOpen] = useState(false);
  const [pendingHtml, setPendingHtml] = useState<string>("");

  const buildPrimaryEditorData = useCallback((): Data<any, any> => {
    if (initialHtml) {
      try {
        return convertHtmlToPuckData(initialHtml);
      } catch (err) {
        console.error("Error parsing initial HTML:", err);
      }
    }
    return getInitialData(t);
  }, [initialHtml]);

  const [editorData, setEditorData] = useState<Data<any, any>>(() =>
    buildPrimaryEditorData(),
  );

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [importMode, setImportMode] = useState<"file" | "paste">("file");
  const [pasteHtml, setPasteHtml] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [exportHtml, setExportHtml] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropFileInputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // ResizeObserver loop è un warning benigno del browser (non un errore funzionale).
  // React Dev Overlay lo intercetta e lo mostra come errore: lo sopprmiamo qui.
  useEffect(() => {
    const handler = (e: ErrorEvent) => {
      if (e.message.includes("ResizeObserver loop")) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  // Sovrascrive height:100dvh nativo di _PuckLayout-inner_ con il valore px reale
  // del container modale, tramite CSS custom property anziché catena height:100%.
  useEffect(() => {
    const root = rootRef.current;
    if (!root?.parentElement) return;
    let rafId: number;
    const updateHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const h = root.parentElement!.clientHeight;
        root.style.setProperty("--puck-available-height", `${h}px`);
        root.style.height = `${h}px`;
      });
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(root.parentElement!);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  const showToast = useCallback((message?: string) => {
    if (message) setToastMessage(message);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 3200);
  }, []);

  const handlePublish = (data: any) => {
    const html = generateEmailHtml(data);

    if (!isEditMode) {
      if (onSave) {
        onSave(html, false);
      }
      showToast("Salvato!");
      return;
    }

    setPendingHtml(html);
    setConfirmPanelOpen(true);
  };

  const handleConfirmSave = (retranslateAll: boolean) => {
    setConfirmPanelOpen(false);
    if (onSave) {
      onSave(pendingHtml, retranslateAll);
    }
    showToast(
      retranslateAll
        ? t("saved-update-in-progress")
        : t("saved-no-update-needed"),
    );
  };

  const normalizeHtml = (html: string): string => {
    const trimmed = html.trim();
    if (!trimmed) return trimmed;

    const hasHtmlTag =
      /<!DOCTYPE\s+html/i.test(trimmed) || /<html[\s>]/i.test(trimmed);
    if (hasHtmlTag) return trimmed;

    const hasBodyTag = /<body[\s>]/i.test(trimmed);
    if (hasBodyTag) {
      return `<!DOCTYPE html>\n<html>\n<head></head>\n${trimmed}\n</html>`;
    }

    return `<!DOCTYPE html>\n<html>\n<head></head>\n<body>\n${trimmed}\n</body>\n</html>`;
  };

  const validateHtml = (html: string): string | null => {
    const trimmed = html.trim();
    if (!trimmed) return "Il contenuto è vuoto.";
    if (!/<!DOCTYPE\s+html/i.test(trimmed) && !/<html[\s>]/i.test(trimmed)) {
      return "Il contenuto non sembra essere un documento HTML valido (manca <!DOCTYPE html> o <html>).";
    }
    return null;
  };

  const applyImport = (htmlContent: string) => {
    const normalized = normalizeHtml(htmlContent);
    const error = validateHtml(normalized);
    if (error) {
      setImportError(error);
      return;
    }
    try {
      const newData = convertHtmlToPuckData(normalized);
      setEditorData(newData);
      setPuckKey((prev) => prev + 1);
      setImportPanelOpen(false);
      setPasteHtml("");
      setImportError(null);
      setSelectedFile(null);
      showToast(t("html-template-imported"));
    } catch (err) {
      console.error("Import error:", err);
      setImportError(t("html-template-import-error"));
    }
  };

  const PRIMARY_LANG_KEY = "__primary__";
  const languageOptions = Object.keys(languageLabels || {});

  const [selectedLang, setSelectedLang] = useState<string>(PRIMARY_LANG_KEY);

  const handleLanguageChange = (lang: string) => {
    setSelectedLang(lang);
    if (lang === PRIMARY_LANG_KEY) {
      setEditorData(buildPrimaryEditorData());
      setPuckKey((prev) => prev + 1);
      return;
    }
    const html = templates?.[lang]?.description;
    try {
      const newData = html
        ? convertHtmlToPuckData(normalizeHtml(html))
        : getInitialData(t);
      setEditorData(newData);
      setPuckKey((prev) => prev + 1);
    } catch (err) {
      console.error("Error switching language template:", err);
    }
  };

  const [openToast, setOpenToast] = useState<
    { s: StatusEnum; message: string } | undefined
  >();

  const handleUpdateSecondaryTemplate = async (html: string) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await onUpdateSecondaryTemplate?.(selectedLang, html);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error("Error updating secondary template:", error);
    }
  };

  const handleImportClick = () => {
    setImportError(null);
    setPasteHtml("");
    setImportMode("file");
    setImportPanelOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDropFile = (file: File) => {
    if (!file.name.match(/\.html?$/i)) {
      setImportError(t("select-html-file"));
      setSelectedFile(null);
      return;
    }
    setImportError(null);
    setSelectedFile(file);
  };

  const handleFileImport = () => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onload = (ev) => applyImport(ev.target?.result as string);
    reader.readAsText(selectedFile);
  };

  const handleDropZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleDropFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleDropFile(file);
    e.target.value = "";
  };

  return (
    <div
      ref={rootRef}
      className="puck-root"
      style={
        {
          display: "flex",
          width: "100%",
          height: "100%",
          flexDirection: "column",
          "--puck-color-azure-04": theme.palette.primary.main,
          "--puck-color-azure-05": theme.palette.primary.dark,
          "--puck-color-azure-06": alpha(theme.palette.primary.main, 0.55),
          "--puck-color-azure-07": alpha(theme.palette.primary.main, 0.4),
          "--puck-color-azure-08": alpha(theme.palette.primary.main, 0.28),
          "--puck-color-azure-09": alpha(theme.palette.primary.main, 0.18),
          "--puck-color-azure-10": alpha(theme.palette.primary.main, 0.1),
          "--puck-color-azure-11": alpha(theme.palette.primary.main, 0.05),
          "--puck-color-azure-12": alpha(theme.palette.primary.main, 0.08),
          "--puck-color-indigo-04": theme.palette.primary.main,
          "--puck-color-black": theme.palette.text.primary,
          "--puck-color-white": theme.palette.background.paper,

          // Puck grey scale mapping (01 is darkest, 13 is lightest)
          "--puck-color-grey-01": theme.palette.text.primary,
          "--puck-color-grey-02": theme.palette.text.primary,
          "--puck-color-grey-03": theme.palette.text.secondary,
          "--puck-color-grey-04": theme.palette.text.secondary,
          "--puck-color-grey-05": theme.palette.text.disabled || "#9ca3af",
          "--puck-color-grey-06": theme.palette.text.disabled || "#9ca3af",
          "--puck-color-grey-07": theme.palette.divider,
          "--puck-color-grey-08": theme.palette.divider,
          "--puck-color-grey-09": theme.palette.divider,
          "--puck-color-grey-10": theme.palette.background.default,
          "--puck-color-grey-11": theme.palette.background.default,
          "--puck-color-grey-12": theme.palette.background.paper,
          "--puck-color-grey-13": theme.palette.background.default,

          "--puck-space-px": "1px", // Fix for some browsers
          "--mail-editor-text": theme.palette.text.primary,
          "--mail-editor-text-muted": theme.palette.text.disabled || "#9ca3af",
          "--mail-editor-bg-hover": theme.palette.background.default,
          "--mail-editor-border": theme.palette.divider,
          "--mail-editor-primary": theme.palette.primary.main,
          "--mail-editor-primary-hover": theme.palette.primary.main + "20",
        } as React.CSSProperties
      }
    >
      <div
        className={`mail-editor-toast${toastVisible ? " show" : ""}`}
        style={{ backgroundColor: theme.palette.primary.main }}
      >
        {toastMessage}
      </div>
      <input
        ref={dropFileInputRef}
        type="file"
        accept=".html,.htm"
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />

      {/* Import HTML panel overlay */}
      {importPanelOpen && (
        <div className="mail-import-overlay">
          <div className="mail-import-panel">
            {/* Panel header */}
            <div className="mail-import-panel__header">
              <span className="mail-import-panel__title">
                {t("html-import")}
              </span>
              <button
                className="mail-import-panel__close"
                onClick={() => {
                  setImportPanelOpen(false);
                  setImportError(null);
                  setSelectedFile(null);
                }}
                aria-label={t("close")}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="mail-import-panel__tabs">
              {(["file", "paste"] as const).map((mode) => (
                <button
                  key={mode}
                  className={`mail-import-panel__tab${importMode === mode ? " mail-import-panel__tab--active" : ""}`}
                  onClick={() => {
                    setImportMode(mode);
                    setImportError(null);
                    setSelectedFile(null);
                  }}
                >
                  {mode === "file" ? t("file-upload") : t("html-paste")}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="mail-import-panel__body">
              {importMode === "file" ? (
                selectedFile ? (
                  <div className="mail-import-file-card">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mail-import-file-card__icon"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <div className="mail-import-file-card__info">
                      <span className="mail-import-file-card__name">
                        {selectedFile.name}
                      </span>
                      <span className="mail-import-file-card__size">
                        {formatFileSize(selectedFile.size)}
                      </span>
                    </div>
                    <button
                      className="mail-import-file-card__remove"
                      onClick={() => {
                        setSelectedFile(null);
                        setImportError(null);
                      }}
                      title={t("file-remove")}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div
                    className={`mail-import-dropzone${isDragging ? " mail-import-dropzone--dragging" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDropZoneDrop}
                    onClick={() => dropFileInputRef.current?.click()}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mail-import-dropzone__icon"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="mail-import-dropzone__title">
                      {t("html-file-drag-drop")}
                    </span>
                    <span className="mail-import-dropzone__sub">
                      {t("or")}{" "}
                      <span className="mail-import-dropzone__browse">
                        {t("choose-file")}
                      </span>
                    </span>
                    <span className="mail-import-dropzone__hint">
                      {t("file-types")}
                    </span>
                  </div>
                )
              ) : (
                <div className="mail-import-paste">
                  <label className="mail-import-paste__label">
                    {t("html-paste-here")}
                  </label>
                  <textarea
                    className="mail-import-paste__textarea"
                    value={pasteHtml}
                    onChange={(e) => {
                      setPasteHtml(e.target.value);
                      setImportError(null);
                    }}
                    placeholder={t("paste-html-or-body-email")}
                    rows={12}
                    spellCheck={false}
                  />
                </div>
              )}

              {importError && (
                <div className="mail-import-error">{importError}</div>
              )}
            </div>

            {/* Panel footer */}
            <div className="mail-import-panel__footer">
              <button
                className="mail-import-btn mail-import-btn--secondary"
                onClick={() => {
                  setImportPanelOpen(false);
                  setImportError(null);
                  setSelectedFile(null);
                }}
              >
                {t("cancel")}
              </button>
              {(importMode === "paste" ||
                (importMode === "file" && selectedFile)) && (
                <button
                  className="mail-import-btn mail-import-btn--primary"
                  onClick={() =>
                    importMode === "file"
                      ? handleFileImport()
                      : applyImport(pasteHtml)
                  }
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: "6px" }}
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {t("import")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {exportPanelOpen && (
        <div className="mail-import-overlay">
          <div className="mail-import-panel">
            <div className="mail-import-panel__header">
              <span className="mail-import-panel__title">Esporta HTML</span>
              <button
                className="mail-import-panel__close"
                onClick={() => setExportPanelOpen(false)}
                aria-label={t("close")}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mail-import-panel__body">
              <div className="mail-import-paste">
                <div className="mail-export-label-row">
                  <label className="mail-import-paste__label">
                    {t("generated-html-code")}
                  </label>
                  <button
                    className="mail-import-panel__copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(exportHtml);
                      showToast(t("clipboard-copied"));
                    }}
                    title={t("copy-code")}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copia
                  </button>
                </div>
                <textarea
                  className="mail-import-paste__textarea"
                  value={exportHtml}
                  readOnly
                  rows={16}
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="mail-import-panel__footer">
              <button
                className="mail-import-btn mail-import-btn--primary"
                onClick={() => {
                  const blob = new Blob([exportHtml], { type: "text/html" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "email.html";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: "6px" }}
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Scarica
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmPanelOpen && (
        <div className="mail-import-overlay">
          <div className="mail-import-panel">
            <div className="mail-import-panel__header">
              <span className="mail-import-panel__title">
                {t("editor-update-translation")}
              </span>
              <button
                className="mail-import-panel__close"
                onClick={() => setConfirmPanelOpen(false)}
                aria-label="Chiudi"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mail-import-panel__body">
              <p style={{ margin: 0, lineHeight: 1.5 }}>
                {t("editor-update-pop-up")}
              </p>
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "13px",
                  color: "var(--mail-editor-text-muted)",
                  lineHeight: 1.5,
                }}
              >
                {t("editor-update-warning")}
              </p>
            </div>

            <div className="mail-import-panel__footer">
              <button
                className="mail-import-btn mail-import-btn--secondary"
                onClick={() => handleConfirmSave(false)}
              >
                {t("editor-update-manual")}
              </button>
              <button
                className="mail-import-btn mail-import-btn--primary"
                onClick={() => handleConfirmSave(true)}
              >
                {t("editor-update-translate-all")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalConfirmsCst
        open={!!openToast}
        title=""
        description=""
        routeToSamePage={true}
        removeButtons={openToast?.s === StatusEnum.LOADING}
        onCallBackClosed={() => setOpenToast(undefined)}
        onActionConfirmed={() => setOpenToast(undefined)}
        status={openToast}
      />

      <Puck
        key={puckKey}
        // config={config}
        config={getPuckConfig(t)}
        headerTitle={
          notificationName
            ? `Template email - ${notificationName}`
            : "Template email"
        }
        data={editorData}
        onPublish={handlePublish}
        overrides={{
          headerActions: ({ children: _children }) => (
            <>
              {languageOptions.length > 0 && (
                <select
                  className="mail-editor-lang-select"
                  value={selectedLang}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  title={t("change-template-language")}
                  style={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  }}
                >
                  <option value={PRIMARY_LANG_KEY}>{t("main-template")}</option>
                  {languageOptions.map((lang) => (
                    <option key={lang} value={lang}>
                      {languageLabels?.[lang] || lang.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
              {selectedLang !== PRIMARY_LANG_KEY && (
                <UpdateSecondaryTemplateButton
                  theme={theme}
                  onUpdate={handleUpdateSecondaryTemplate}
                />
              )}
              <button
                className="mail-editor-import-btn"
                onClick={handleImportClick}
                title={t("html-template-import")}
                style={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: "6px" }}
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t("html-import")}
              </button>
              <ExportButton
                theme={theme}
                onExport={(html) => {
                  setExportHtml(html);
                  setExportPanelOpen(true);
                }}
              />
              {selectedLang === PRIMARY_LANG_KEY && (
                <SaveButton onPublish={handlePublish} theme={theme} />
              )}

              {onClose && (
                <button
                  className="mail-editor-close-btn"
                  onClick={onClose}
                  title={t("close-editor")}
                  style={{
                    marginLeft: "8px",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("close")}
                </button>
              )}
            </>
          ),
          outline: ({ children }) => (
            <div>
              <RootOutlineItem />
              {children}
            </div>
          ),
          iframe: ({ document: iframeDoc, children }) => (
            <IframeResizer document={iframeDoc}>{children}</IframeResizer>
          ),
        }}
      />
    </div>
  );
};

export default MailEditor;
